import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupIcon from "@mui/icons-material/Group";
import RefreshIcon from "@mui/icons-material/Refresh";
import VideoCameraFrontIcon from "@mui/icons-material/VideoCameraFront";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { SidebarCol } from "../../../screens/chatMain/Sidebar/Sidebar.jsx";
import { MainHeader } from "../../../screens/chatMain/MainHeader/MainHeader.jsx";
import {
  fetchAdminLiveEvents,
  deleteAdminLiveEvent,
  fetchLiveEventAttendees,
} from "../../../api/dev/liveEventsAdminApi";
import { LiveEventFormDialog } from "./LiveEventFormDialog";
import { LiveEventAttendeesDialog } from "./LiveEventAttendeesDialog";

const DevLiveEvents = () => {
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusTab, setStatusTab] = useState("all");

  // Create / Edit modal state
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedEventForEdit, setSelectedEventForEdit] = useState(null);

  // Attendees modal state
  const [attendeesDialogOpen, setAttendeesDialogOpen] = useState(false);
  const [selectedEventForAttendees, setSelectedEventForAttendees] = useState(null);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteAttendees, setDeleteAttendees] = useState([]);
  const [deleteAttendeesLoading, setDeleteAttendeesLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const res = await fetchAdminLiveEvents();
      if (res && res.success) {
        setEvents(res.data || []);
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.error || "Failed to fetch live events list"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const handleOpenCreate = () => {
    setSelectedEventForEdit(null);
    setFormDialogOpen(true);
  };

  const handleOpenEdit = (event) => {
    setSelectedEventForEdit(event);
    setFormDialogOpen(true);
  };

  const handleOpenAttendees = (event) => {
    setSelectedEventForAttendees(event);
    setAttendeesDialogOpen(true);
  };

  const handleOpenDelete = async (event) => {
    setDeleteTarget(event);
    setDeleteAttendees([]);
    setDeleteAttendeesLoading(true);
    try {
      const res = await fetchLiveEventAttendees(event.id || event.event_id);
      if (res && res.success) {
        setDeleteAttendees(res.attendees || []);
      }
    } catch (err) {
      console.error("Failed to fetch attendees for delete target:", err);
    } finally {
      setDeleteAttendeesLoading(false);
    }
  };

  const handleDeleteConfirm = async (notifyAttendees = false) => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await deleteAdminLiveEvent(deleteTarget.id || deleteTarget.event_id, notifyAttendees);
      if (notifyAttendees && deleteAttendees.length > 0) {
        toast.success(
          `Event "${deleteTarget.title}" deleted & cancellation notices sent to ${deleteAttendees.length} registered attendee(s)!`
        );
      } else {
        toast.success(`Event "${deleteTarget.title}" deleted successfully!`);
      }
      setDeleteTarget(null);
      setDeleteAttendees([]);
      await loadEvents();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.error || "Failed to delete live event"
      );
    } finally {
      setDeleting(false);
    }
  };

  const now = new Date();

  const filteredEvents = events.filter((ev) => {
    // Search query match
    const q = searchQuery.toLowerCase();
    const title = (ev.title || "").toLowerCase();
    const presenter = (ev.presenter || "").toLowerCase();
    const cat = (ev.category || "").toLowerCase();
    const matchesSearch = title.includes(q) || presenter.includes(q) || cat.includes(q);

    if (!matchesSearch) return false;

    // Status / Time tab match
    const evDate = ev.event_date ? new Date(ev.event_date) : null;
    const isPast = evDate && evDate < now;
    const isUpcoming = evDate && evDate >= now;

    if (statusTab === "all") return true;
    if (statusTab === "upcoming") return isUpcoming && ev.status === "Active";
    if (statusTab === "past") return isPast;
    if (statusTab === "draft") return ev.status === "Draft";
    if (statusTab === "cancelled") return ev.status === "Cancelled";
    if (statusTab === "active") return ev.status === "Active" && !isPast;

    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "–";
    try {
      const d = new Date(dateStr);
      return d.toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return dateStr;
    }
  };

  const getStatusChipProps = (status, dateStr) => {
    const d = dateStr ? new Date(dateStr) : null;
    const isPast = d && d < now;

    if (status === "Cancelled") {
      return { label: "Cancelled", color: "#DC2626", bg: "#FEF2F2", border: "#FCA5A5" };
    }
    if (status === "Draft") {
      return { label: "Draft", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A" };
    }
    if (isPast) {
      return { label: "Completed", color: "#64748B", bg: "#F1F5F9", border: "#CBD5E1" };
    }
    return { label: "Active", color: "#059669", bg: "#ECFDF5", border: "#A7F3D0" };
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100vw",
        height: "100vh",
        maxWidth: "100vw",
        maxHeight: "100vh",
        overflow: "hidden",
        background: "#F8FAFC",
      }}
    >
      {/* Sidebar */}
      <Box sx={{ flexShrink: 0 }}>
        <SidebarCol />
      </Box>

      {/* Main Area */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
        }}
      >
        <MainHeader
          page="dev"
          titleText="Live Events Management"
          subtitleText="Create sessions, manage capacity & oversee attendees"
          back={() => {
            if (window.history.state && window.history.state.idx > 0) {
              navigate(-1);
            } else {
              navigate("/dev");
            }
          }}
        />

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 3,
            pb: 4,
            pt: 3,
          }}
        >
          {/* Breadcrumbs */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <Typography variant="caption" sx={{ color: "#9ca3af" }}>
              Home
            </Typography>
            <Typography variant="caption" sx={{ color: "#9ca3af" }}>
              /
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#9ca3af", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
              onClick={() => navigate("/dev")}
            >
              Developer
            </Typography>
            <Typography variant="caption" sx={{ color: "#9ca3af" }}>
              /
            </Typography>
            <Typography variant="caption" sx={{ color: "#374151", fontWeight: 600 }}>
              Live Events
            </Typography>
          </Box>

          {/* Action Header Banner */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="h5" fontWeight={700} color="#1E293B">
                Live Events & Group Guidance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {events.length} total events configured across all schedules
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <IconButton
                onClick={loadEvents}
                disabled={loading}
                sx={{
                  bgcolor: "#FFFFFF",
                  border: "1px solid #E2E8F0",
                  borderRadius: 2,
                  "&:hover": { bgcolor: "#F1F5F9" },
                }}
                title="Refresh events"
              >
                <RefreshIcon fontSize="small" sx={{ color: "#475569" }} />
              </IconButton>
              <Button
                variant="contained"
                startIcon={<AddIcon sx={{ color: "#FFFFFF !important" }} />}
                onClick={handleOpenCreate}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.9375rem",
                  color: "#FFFFFF !important",
                  background: "linear-gradient(180deg, #0C8F63 0%, #0A7253 100%)",
                  px: 2.5,
                  py: 1,
                  boxShadow: "0 4px 12px rgba(10, 114, 83, 0.25)",
                  "&:hover": {
                    background: "linear-gradient(180deg, #06442F 0%, #085A42 100%)",
                    boxShadow: "0 6px 16px rgba(10, 114, 83, 0.35)",
                  },
                }}
              >
                Add New Live Event
              </Button>
            </Box>
          </Box>

          {/* Filters & Search Row */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 3,
              border: "1px solid #E2E8F0",
              bgcolor: "#FFFFFF",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            {/* Status Tabs */}
            <Tabs
              value={statusTab}
              onChange={(_, v) => setStatusTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 40,
                "& .MuiTab-root": {
                  minHeight: 40,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  px: 2,
                  borderRadius: 2,
                },
                "& .Mui-selected": {
                  color: "#154C3A !important",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#154C3A",
                  height: 3,
                  borderRadius: 1.5,
                },
              }}
            >
              <Tab label={`All (${events.length})`} value="all" />
              <Tab
                label={`Upcoming (${events.filter((e) => e.event_date && new Date(e.event_date) >= now && e.status === "Active").length})`}
                value="upcoming"
              />
              <Tab
                label={`Active (${events.filter((e) => e.status === "Active" && (!e.event_date || new Date(e.event_date) >= now)).length})`}
                value="active"
              />
              <Tab
                label={`Draft (${events.filter((e) => e.status === "Draft").length})`}
                value="draft"
              />
              <Tab
                label={`Past (${events.filter((e) => e.event_date && new Date(e.event_date) < now).length})`}
                value="past"
              />
              <Tab
                label={`Cancelled (${events.filter((e) => e.status === "Cancelled").length})`}
                value="cancelled"
              />
            </Tabs>

            {/* Search Input */}
            <TextField
              placeholder="Search by title, presenter, or category..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#94A3B8" }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2, minWidth: 280, bgcolor: "#F8FAFC" },
              }}
            />
          </Paper>

          {/* Events Data Table */}
          {loading ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 10 }}>
              <CircularProgress size={40} sx={{ color: "#154C3A", mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading live events...
              </Typography>
            </Box>
          ) : filteredEvents.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 6,
                textAlign: "center",
                borderRadius: 3,
                border: "1px dashed #CBD5E1",
                bgcolor: "#FFFFFF",
              }}
            >
              <VideoCameraFrontIcon sx={{ fontSize: 50, color: "#CBD5E1", mb: 1.5 }} />
              <Typography variant="h6" fontWeight={600} color="#475569">
                {searchQuery ? "No matching live events found" : "No live events created yet"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                {searchQuery
                  ? "Try refining your search terms or selecting a different status filter."
                  : "Click 'Add New Live Event' above to create your first session."}
              </Typography>
              {!searchQuery && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon sx={{ color: "#FFFFFF !important" }} />}
                  onClick={handleOpenCreate}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    color: "#FFFFFF !important",
                    background: "linear-gradient(180deg, #0C8F63 0%, #0A7253 100%)",
                    px: 2.5,
                    py: 1,
                    boxShadow: "0 4px 12px rgba(10, 114, 83, 0.25)",
                    "&:hover": {
                      background: "linear-gradient(180deg, #06442F 0%, #085A42 100%)",
                      boxShadow: "0 6px 16px rgba(10, 114, 83, 0.35)",
                    },
                  }}
                >
                  Add Live Event
                </Button>
              )}
            </Paper>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #E2E8F0",
                bgcolor: "#FFFFFF",
                boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ "& th": { bgcolor: "#F8FAFC", fontWeight: 700, color: "#475569" } }}>
                    <TableCell width={60}>ID</TableCell>
                    <TableCell>Event Title & Category</TableCell>
                    <TableCell>Presenter</TableCell>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Capacity & Registered</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right" width={180}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEvents.map((ev) => {
                    const chip = getStatusChipProps(ev.status, ev.event_date);
                    const filled = ev.filled_count || 0;
                    const cap = ev.capacity || 100;
                    const isFull = filled >= cap;

                    return (
                      <TableRow
                        key={ev.id || ev.event_id}
                        hover
                        sx={{ "&:last-child td": { borderBottom: 0 } }}
                      >
                        {/* ID */}
                        <TableCell sx={{ fontWeight: 600, color: "#94A3B8" }}>
                          #{ev.id || ev.event_id}
                        </TableCell>

                        {/* Title & Category */}
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={700} color="#1E293B">
                              {ev.title}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                              <Chip
                                label={ev.category || "General"}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  bgcolor: "#EEF2FF",
                                  color: "#4F46E5",
                                }}
                              />
                              {ev.recur_interval && ev.recur_interval !== "None" && (
                                <Chip
                                  label={`🔄 ${ev.recur_interval}`}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.7rem",
                                    fontWeight: 500,
                                    bgcolor: "#F1F5F9",
                                    color: "#475569",
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Presenter */}
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} color="#334155">
                            {ev.presenter || "–"}
                          </Typography>
                          {ev.presenter_email && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#0284C7",
                                display: "block",
                                fontSize: "0.75rem",
                                wordBreak: "break-all",
                              }}
                            >
                              ✉️ {ev.presenter_email}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Date & Time */}
                        <TableCell>
                          <Typography variant="body2" color="#1E293B" fontWeight={500}>
                            {formatDate(ev.event_date)}
                          </Typography>
                        </TableCell>

                        {/* Duration */}
                        <TableCell>
                          <Typography variant="body2" color="#64748B">
                            {ev.duration_minutes} mins
                          </Typography>
                        </TableCell>

                        {/* Capacity / Filled */}
                        <TableCell>
                          <Tooltip title={`${filled} registered out of ${cap} maximum seats`}>
                            <Chip
                              icon={<GroupIcon sx={{ fontSize: "14px !important" }} />}
                              label={`${filled} / ${cap}`}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                bgcolor: isFull ? "#FEF2F2" : filled > 0 ? "#ECFDF5" : "#F8FAFC",
                                color: isFull ? "#DC2626" : filled > 0 ? "#059669" : "#64748B",
                                border: "1px solid",
                                borderColor: isFull ? "#FCA5A5" : filled > 0 ? "#A7F3D0" : "#E2E8F0",
                              }}
                            />
                          </Tooltip>
                        </TableCell>

                        {/* Status */}
                        <TableCell>
                          <Chip
                            label={chip.label}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              bgcolor: chip.bg,
                              color: chip.color,
                              border: `1px solid ${chip.border}`,
                            }}
                          />
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right">
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                            {/* Manage Attendees (Hidden - rosters are now dispatched automatically to presenter) */}
                            {/*
                            <Tooltip title="View registered attendees & email roster">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenAttendees(ev)}
                                sx={{
                                  color: "#059669",
                                  bgcolor: "#ECFDF5",
                                  "&:hover": { bgcolor: "#D1FAE5" },
                                }}
                              >
                                <GroupIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            */}

                            {/* Edit */}
                            <Tooltip title="Edit event details">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEdit(ev)}
                                sx={{
                                  color: "#2563EB",
                                  bgcolor: "#EFF6FF",
                                  "&:hover": { bgcolor: "#DBEAFE" },
                                }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            {/* Delete */}
                            <Tooltip title="Delete event">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDelete(ev)}
                                sx={{
                                  color: "#DC2626",
                                  bgcolor: "#FEF2F2",
                                  "&:hover": { bgcolor: "#FEE2E2" },
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>

      {/* Create / Edit Dialog */}
      <LiveEventFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        event={selectedEventForEdit}
        onSuccess={loadEvents}
      />

      {/* Attendees Dialog */}
      <LiveEventAttendeesDialog
        open={attendeesDialogOpen}
        onClose={() => setAttendeesDialogOpen(false)}
        eventId={selectedEventForAttendees?.id || selectedEventForAttendees?.event_id}
        onAttendeeChanged={loadEvents}
      />

      {/* Delete Confirmation Dialog with Attendee Cancellation Notification */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => (deleting ? undefined : setDeleteTarget(null))}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #FEE2E2",
            py: 2,
            px: 3,
            bgcolor: "#FEF2F2",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningAmberIcon sx={{ color: "#DC2626", fontSize: 24 }} />
            <Typography variant="h6" fontWeight={700} color="#DC2626">
              Delete Live Event?
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Event Summary Card */}
          <Box
            sx={{
              p: 2,
              bgcolor: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderRadius: 2,
              mb: 2.5,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} color="#1E293B">
              {deleteTarget?.title}
            </Typography>
            <Typography variant="body2" color="#64748B" sx={{ mt: 0.5 }}>
              <strong>Presenter:</strong> {deleteTarget?.presenter || "–"} &bull;{" "}
              <strong>Scheduled:</strong> {formatDate(deleteTarget?.event_date)}
            </Typography>
          </Box>

          {/* Attendees Status & Warning */}
          {deleteAttendeesLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                py: 2,
              }}
            >
              <CircularProgress size={20} sx={{ color: "#DC2626" }} />
              <Typography variant="body2" color="#64748B">
                Checking registered attendees...
              </Typography>
            </Box>
          ) : deleteAttendees.length > 0 ? (
            <Box
              sx={{
                bgcolor: "#FFFBEB",
                border: "1px solid #FDE68A",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <GroupIcon sx={{ color: "#D97706", fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={700} color="#B45309">
                  {deleteAttendees.length} Registered User{deleteAttendees.length > 1 ? "s" : ""} for this Session
                </Typography>
              </Box>

              <Typography variant="body2" color="#92400E" sx={{ mb: 1 }}>
                The following users are currently registered:
              </Typography>

              {/* Scrollable Attendees List */}
              <Box
                sx={{
                  maxHeight: 130,
                  overflowY: "auto",
                  bgcolor: "#FFFFFF",
                  borderRadius: 1.5,
                  border: "1px solid #FCD34D",
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.8,
                }}
              >
                {deleteAttendees.map((att, idx) => (
                  <Box
                    key={att.registration_id || idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 1,
                      py: 0.5,
                      bgcolor: "#FEF3C7",
                      borderRadius: 1,
                      fontSize: "12px",
                    }}
                  >
                    <Typography variant="caption" fontWeight={600} color="#1E293B">
                      {att.user_name || "User"}
                    </Typography>
                    <Typography variant="caption" color="#64748B">
                      {att.email || "No email"}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Typography
                variant="body2"
                fontWeight={700}
                color="#B45309"
                sx={{ mt: 2 }}
              >
                Do you need to inform registered users that this meeting is cancelled?
              </Typography>
              <Typography variant="caption" color="#78350F" sx={{ display: "block", mt: 0.5 }}>
                • <strong>Yes</strong>: Sends official cancellation emails with calendar cancellation to all registered users, then deletes all data from the database.
                <br />
                • <strong>Skip</strong>: Deletes the live event and registration data immediately without sending any emails.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                bgcolor: "#F1F5F9",
                border: "1px solid #E2E8F0",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="body2" color="#475569">
                No users are currently registered for this session. Deleting will permanently remove the live event from the database.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 2.5,
            borderTop: "1px solid #E2E8F0",
            bgcolor: "#F8FAFC",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              color: "#64748B",
              borderColor: "#CBD5E1",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>

          {deleteAttendees.length > 0 ? (
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <Button
                onClick={() => handleDeleteConfirm(false)}
                disabled={deleting || deleteAttendeesLoading}
                variant="outlined"
                color="inherit"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#64748B",
                  borderColor: "#CBD5E1",
                  "&:hover": { bgcolor: "#F1F5F9", borderColor: "#94A3B8" },
                }}
              >
                Skip & Delete (No Email)
              </Button>
              <Button
                onClick={() => handleDeleteConfirm(true)}
                disabled={deleting || deleteAttendeesLoading}
                variant="contained"
                startIcon={
                  deleting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <MailOutlineIcon fontSize="small" />
                  )
                }
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: "#DC2626",
                  "&:hover": { bgcolor: "#B91C1C" },
                }}
              >
                {deleting ? "Notifying & Deleting..." : "Yes, Send Cancellation Email & Delete"}
              </Button>
            </Box>
          ) : (
            <Button
              onClick={() => handleDeleteConfirm(false)}
              disabled={deleting || deleteAttendeesLoading}
              variant="contained"
              startIcon={
                deleting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DeleteOutlineIcon fontSize="small" />
                )
              }
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "#DC2626",
                "&:hover": { bgcolor: "#B91C1C" },
              }}
            >
              {deleting ? "Deleting..." : "Delete Event"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DevLiveEvents;
